import { useState, useMemo, useCallback, useEffect } from 'react'
import { isAllBlanksFilled } from '../../../utils/quiz-parser'
import type { DragTextQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

interface DragTextChoice {
  key?: string | null
  answer: string
}

type DragTextAnswerSource = string | DragTextChoice

export interface DragTextOption {
  key: string
  label: string
}

export interface UseDragTextQuizOptions {
  quizId: string | number
  quiz: DragTextQuizData & { choices?: DragTextChoice[]; answers?: DragTextAnswerSource[] }
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

function parseAnswerKey(key: string): string[] {
  try {
    const parsed = JSON.parse(key)
    return Array.isArray(parsed) ? parsed.map((value) => String(value)) : []
  } catch {
    return []
  }
}

function toOption(source: DragTextAnswerSource): DragTextOption | null {
  if (typeof source === 'string') {
    const label = source.trim()
    return label ? { key: label, label } : null
  }

  if (!source || typeof source.answer !== 'string') return null

  const label = source.answer.trim()
  if (!label) return null

  return { key: typeof source.key === 'string' && source.key ? source.key : label, label }
}

function dedupeOptions(options: DragTextOption[]): DragTextOption[] {
  const seen = new Set<string>()
  const result: DragTextOption[] = []

  options.forEach(option => {
    const identity = `${option.key}\u0000${option.label}`
    if (seen.has(identity)) return
    seen.add(identity)
    result.push(option)
  })

  return result
}

function extractProblemOptions(problem: string): DragTextOption[] {
  const options: DragTextOption[] = []
  const answerReg = /\{\{([^{}]*)}}/g
  let match: RegExpExecArray | null

  while ((match = answerReg.exec(problem)) !== null) {
    const raw = match[1]?.trim()
    if (!raw || /^`.+`$/.test(raw)) continue

    const label = raw.includes(':') ? raw.slice(raw.indexOf(':') + 1).trim() : raw
    if (label) options.push({ key: label, label })
  }

  return dedupeOptions(options)
}

function countDragTextBlanks(problem: string): number {
  const answerReg = /\{\{([^{}]*)}}/g
  let count = 0
  let match: RegExpExecArray | null

  while ((match = answerReg.exec(problem)) !== null) {
    const raw = match[1]?.trim() ?? ''
    if (/^`.+`$/.test(raw)) continue
    count++
  }

  return count
}

export function useDragTextQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseDragTextQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const blankCount = useMemo(() => countDragTextBlanks(quiz.problem), [quiz.problem])
  const problemKey = useMemo(
    () => `${quizId}-${blankCount}-${quiz.problem.length}`,
    [quizId, blankCount, quiz.problem],
  )
  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers
  const hasInitialAnswers = initialAnswersProp !== undefined
  const initialAnswersKey = JSON.stringify(initialAnswersProp ?? [])
  const correctAnswersKey = JSON.stringify(solveResult?.correctAnswers ?? [])

  const availableOptions = useMemo<DragTextOption[]>(() => {
    if (quiz.choices && Array.isArray(quiz.choices) && quiz.choices.length > 0) {
      return dedupeOptions(quiz.choices.map(toOption).filter((o): o is DragTextOption => Boolean(o)))
    }
    if (quiz.pairs?.right && Array.isArray(quiz.pairs.right) && quiz.pairs.right.length > 0) {
      return dedupeOptions(quiz.pairs.right.map(toOption).filter((o): o is DragTextOption => Boolean(o)))
    }
    if (quiz.answers && Array.isArray(quiz.answers) && quiz.answers.length > 0) {
      return dedupeOptions(quiz.answers.map(toOption).filter((o): o is DragTextOption => Boolean(o)))
    }
    return extractProblemOptions(quiz.problem)
  }, [quiz.answers, quiz.choices, quiz.pairs, quiz.problem])

  const optionLabelByKey = useMemo(() => {
    const map = new Map<string, string>()
    availableOptions.forEach(o => map.set(o.key, o.label))
    return map
  }, [availableOptions])

  const optionKeyByLabel = useMemo(() => {
    const map = new Map<string, string>()
    availableOptions.forEach(o => {
      if (!map.has(o.label)) map.set(o.label, o.key)
    })
    return map
  }, [availableOptions])

  const normalizeValueToKey = useCallback((value: string) => {
    if (!value) return ''
    if (optionLabelByKey.has(value)) return value
    return optionKeyByLabel.get(value) ?? value
  }, [optionKeyByLabel, optionLabelByKey])

  const resolveLabel = useCallback((value: string) => {
    if (!value) return ''
    return optionLabelByKey.get(value) ?? value
  }, [optionLabelByKey])

  const computedInitialAnswers = useMemo(() => {
    const initialAnswers = parseAnswerKey(initialAnswersKey)
    const correctAnswers = parseAnswerKey(correctAnswersKey)
    const base =
      hasInitialAnswers
        ? initialAnswers
        : isViewSolved && correctAnswers.length > 0
          ? correctAnswers
          : new Array(blankCount).fill('')

    const normalized = new Array(blankCount).fill('').map((_, idx) => normalizeValueToKey(base[idx] ?? ''))
    return normalized
  }, [blankCount, correctAnswersKey, hasInitialAnswers, initialAnswersKey, isViewSolved, normalizeValueToKey])

  const [answers, setAnswers] = useState<string[]>(computedInitialAnswers)
  const canSubmit = isAllBlanksFilled(answers)

  useEffect(() => {
    setAnswers(prev => {
      if (
        prev.length === computedInitialAnswers.length &&
        prev.every((v, i) => v === computedInitialAnswers[i])
      ) {
        return prev
      }
      return computedInitialAnswers
    })
  }, [computedInitialAnswers])

  useEffect(() => {
    onAnswerChange?.(answers)
  }, [answers, onAnswerChange])

  const handleBlankClick = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setCurrentIndex(index)
  }, [isSolved, isViewSolved])

  const handleClearBlank = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setAnswers(prev => {
      const newAnswers = [...prev]
      newAnswers[index] = ''
      return newAnswers
    })
  }, [isSolved, isViewSolved])

  const handleSelectOption = useCallback((optionKey: string) => {
    if (isSolved || isViewSolved) return
    setAnswers(prev => {
      const newAnswers = [...prev]
      newAnswers[currentIndex] = optionKey
      const nextEmptyIndex = newAnswers.findIndex((a, i) => i > currentIndex && a === '')
      if (nextEmptyIndex !== -1) {
        setCurrentIndex(nextEmptyIndex)
      } else {
        const firstEmptyIndex = newAnswers.findIndex((a, i) => i !== currentIndex && a === '')
        if (firstEmptyIndex !== -1) {
          setCurrentIndex(firstEmptyIndex)
        }
      }
      return newAnswers
    })
  }, [currentIndex, isSolved, isViewSolved])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, answers, quiz.vars, elapsedTime)
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime])

  return {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
